import { useState, useEffect } from 'react'

interface ContentItem {
  id: string
  title: string
  description: string
  type: 'presentation' | 'pdf' | 'image' | 'video' | 'markdown'
  filePath: string
  thumbnailUrl: string | null
  accessLevel: 'demo' | 'company' | 'admin'
  companyId: string | null
  metadata: Record<string, any> | null
  createdAt: string
  updatedAt: string
}

interface ContentResponse {
  content: ContentItem[]
  pagination: {
    page: number
    limit: number
    totalCount: number
    totalPages: number
    hasNextPage: boolean
    hasPrevPage: boolean
  }
}

interface UseCompanyContentResult {
  content: ContentItem[]
  loading: boolean
  error: string | null
  refetch: () => void
}

export function useCompanyContent(): UseCompanyContentResult {
  const [content, setContent] = useState<ContentItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchContent = async () => {
    setLoading(true)
    setError(null)

    try {
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('http://localhost:3000/api/content', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch content')
      }

      const data: ContentResponse = await response.json()
      setContent(data.content)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
      console.error('Error fetching company content:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchContent()
  }, [])

  return { content, loading, error, refetch: fetchContent }
}

export type { ContentItem, ContentResponse, UseCompanyContentResult }
